import React, { useState } from "react";
import { motion } from "framer-motion";
import {
	MessageCircle,
	Video,
	Baby,
	Shield,
	Calendar,
	Clock,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

interface AdminMessageThreadProps {
	conversationData: {
		id: string;
		parent_user: {
			id: string;
			full_name: string;
			email: string;
			avatar_url: string | null;
		};
		doula_user: {
			id: string;
			full_name: string;
			email: string;
			avatar_url: string | null;
		};
		appointment?: {
			id: string;
			appointment_date: string;
			status: string;
			duration_minutes?: number;
		};
		appointments?: Array<{
			id: string;
			appointment_date: string;
			status: string;
			duration_minutes?: number;
			google_meet_link?: string | null;
			notes?: string | null;
		}>;
		created_at: string;
	};
	messages: Array<{
		id: string;
		content: string;
		created_at: string;
		sender_type: "parent" | "doula";
		sender_name: string;
		is_flagged: boolean;
	}>;
	loading?: boolean;
}

const AdminMessageThread: React.FC<AdminMessageThreadProps> = ({
	conversationData,
	messages,
	loading = false,
}) => {
	const [activeTab, setActiveTab] = useState<"messages" | "meeting">(
		"messages",
	);

	const formatTime = (dateString: string) => {
		return new Date(dateString).toLocaleTimeString("en-US", {
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		});
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	const getStatusBadge = (status: string) => {
		const statusConfig = {
			pending: {
				color: "bg-yellow-100 text-yellow-800 border-yellow-200",
				label: "Pending",
			},
			accepted: {
				color: "bg-green-100 text-green-800 border-green-200",
				label: "Accepted",
			},
			declined: {
				color: "bg-red-100 text-red-800 border-red-200",
				label: "Declined",
			},
			cancelled: {
				color: "bg-gray-100 text-gray-800 border-gray-200",
				label: "Cancelled",
			},
			completed: {
				color: "bg-blue-100 text-blue-800 border-blue-200",
				label: "Completed",
			},
		};

		const config =
			statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;

		return (
			<Badge className={`${config.color} text-xs font-karla border`}>
				{config.label}
			</Badge>
		);
	};

	return (
		<Card className="glass-card border-0 shadow-lg h-[600px] flex flex-col rounded-lg">
			{/* Conversation Header */}
			<div className="p-4 border-b bg-gray-50">
				<div className="space-y-4">
					{/* Participants */}
					<div className="flex items-center justify-between">
						<div className="flex items-center space-x-3">
							<div className="flex items-center space-x-2">
								<Baby className="h-4 w-4 text-blue-600" />
								<Avatar className="h-8 w-8">
									<AvatarImage
										src={conversationData.parent_user.avatar_url || undefined}
									/>
									<AvatarFallback className="text-xs bg-blue-100 text-blue-600">
										{conversationData.parent_user.full_name.charAt(0)}
									</AvatarFallback>
								</Avatar>
								<div>
									<p className="font-semibold text-sm text-gray-900 font-karla">
										{conversationData.parent_user.full_name}
									</p>
									<p className="text-xs text-gray-500 font-karla">Parent</p>
								</div>
							</div>
						</div>

						<div className="flex items-center space-x-3">
							<div className="flex items-center space-x-2">
								<Shield className="h-4 w-4 text-purple-600" />
								<Avatar className="h-8 w-8">
									<AvatarImage
										src={conversationData.doula_user.avatar_url || undefined}
									/>
									<AvatarFallback className="text-xs bg-purple-100 text-purple-600">
										{conversationData.doula_user.full_name.charAt(0)}
									</AvatarFallback>
								</Avatar>
								<div>
									<p className="font-semibold text-sm text-gray-900 font-karla">
										{conversationData.doula_user.full_name}
									</p>
									<p className="text-xs text-gray-500 font-karla">Doula</p>
								</div>
							</div>
						</div>
					</div>

					{/* Appointment Info */}
					<div className="flex items-center justify-between bg-white p-3 rounded-lg border">
						<div className="flex items-center space-x-2">
							<Calendar className="h-4 w-4 text-gray-600" />
							<span className="text-sm font-medium text-gray-900 font-karla">
								{formatDate(conversationData.appointment.appointment_date)}
							</span>
							<Clock className="h-4 w-4 text-gray-600 ml-2" />
							<span className="text-sm text-gray-600 font-karla">
								{formatTime(conversationData.appointment.appointment_date)}
							</span>
						</div>
						{getStatusBadge(conversationData.appointment.status)}
					</div>
				</div>

				{/* Tab Navigation */}
				<div className="flex mt-4">
					<Button
						variant={activeTab === "messages" ? "default" : "ghost"}
						onClick={() => setActiveTab("messages")}
						className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary-600"
					>
						<MessageCircle className="h-4 w-4 mr-2" />
						Messages ({messages.length})
					</Button>
					<Button
						variant={activeTab === "meeting" ? "default" : "ghost"}
						onClick={() => setActiveTab("meeting")}
						className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary-600"
					>
						<Video className="h-4 w-4 mr-2" />
						Meetings (
						{conversationData.appointments?.length ||
							(conversationData.appointment ? 1 : 0)}
						)
					</Button>
				</div>
			</div>

			{/* Tab Content */}
			<div className="flex-1 overflow-hidden">
				{activeTab === "messages" ? (
					<div className="p-4 h-full overflow-y-auto">
						{loading ? (
							<div className="flex items-center justify-center h-full">
								<div className="text-center">
									<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto mb-4"></div>
									<p className="text-gray-600 font-karla">
										Loading messages...
									</p>
								</div>
							</div>
						) : messages.length === 0 ? (
							<div className="flex items-center justify-center h-full">
								<div className="text-center">
									<MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
									<p className="text-gray-600 font-karla">
										No messages in this conversation
									</p>
								</div>
							</div>
						) : (
							<div className="space-y-4">
								{messages.map((message, index) => (
									<motion.div
										key={message.id}
										initial={{ opacity: 0, y: 10 }}
										animate={{ opacity: 1, y: 0 }}
										transition={{ duration: 0.3, delay: index * 0.05 }}
										className={`flex ${message.sender_type === "parent" ? "justify-start" : "justify-end"}`}
									>
										<div
											className={`max-w-[70%] ${message.sender_type === "parent" ? "order-2" : "order-1"}`}
										>
											<div
												className={`p-3 rounded-lg ${
													message.sender_type === "parent"
														? "bg-blue-50 border border-blue-100"
														: "bg-purple-50 border border-purple-100"
												} ${message.is_flagged ? "ring-2 ring-red-200 bg-red-50" : ""}`}
											>
												<div className="flex items-center justify-between mb-1">
													<span
														className={`text-xs font-medium ${
															message.sender_type === "parent"
																? "text-blue-600"
																: "text-purple-600"
														} font-karla`}
													>
														{message.sender_name}
													</span>
													<div className="flex items-center space-x-1">
														<span className="text-xs text-gray-500 font-karla">
															{formatTime(message.created_at)}
														</span>
														{message.is_flagged && (
															<Badge variant="destructive" className="text-xs">
																Flagged
															</Badge>
														)}
													</div>
												</div>
												<p className="text-sm text-gray-700 font-karla">
													{message.content}
												</p>
											</div>
										</div>

										<div
											className={`${message.sender_type === "parent" ? "order-1 mr-2" : "order-2 ml-2"}`}
										>
											<Avatar className="h-6 w-6">
												<AvatarImage
													src={
														message.sender_type === "parent"
															? conversationData.parent_user.avatar_url ||
																undefined
															: conversationData.doula_user.avatar_url ||
																undefined
													}
												/>
												<AvatarFallback
													className={`text-xs ${
														message.sender_type === "parent"
															? "bg-blue-100 text-blue-600"
															: "bg-purple-100 text-purple-600"
													}`}
												>
													{message.sender_name.charAt(0)}
												</AvatarFallback>
											</Avatar>
										</div>
									</motion.div>
								))}
							</div>
						)}
					</div>
				) : (
					<div className="p-4 h-full overflow-y-auto">
						{/* Show appointments if we have them, otherwise show the single appointment */}
						{conversationData.appointments ? (
							// Multiple appointments view
							conversationData.appointments.length === 0 ? (
								<div className="flex items-center justify-center h-full">
									<div className="text-center">
										<Video className="h-12 w-12 text-gray-400 mx-auto mb-4" />
										<h3 className="text-lg font-medium text-gray-900 font-karla mb-2">
											No Meetings Scheduled
										</h3>
										<p className="text-gray-600 font-karla">
											No appointments have been scheduled for this conversation
											yet.
										</p>
									</div>
								</div>
							) : (
								<div className="space-y-4">
									<div className="text-center mb-6">
										<h3 className="text-lg font-medium text-gray-900 font-karla mb-2">
											Scheduled Meetings
										</h3>
										<p className="text-gray-600 font-karla">
											All appointments for this conversation
										</p>
									</div>

									{conversationData.appointments.map((appointment, index) => (
										<motion.div
											key={appointment.id}
											initial={{ opacity: 0, y: 10 }}
											animate={{ opacity: 1, y: 0 }}
											transition={{ duration: 0.3, delay: index * 0.1 }}
										>
											<Card className="glass-card border-0 shadow-md bg-white/80 backdrop-blur-sm">
												<div className="p-4">
													<div className="flex items-start justify-between mb-4">
														<div className="flex items-center space-x-3">
															<div className="p-2 bg-primary-100 rounded-full">
																<Calendar className="h-5 w-5 text-primary-600" />
															</div>
															<div>
																<h4 className="font-semibold text-gray-900 font-karla">
																	Appointment #{index + 1}
																</h4>
																<p className="text-sm text-gray-600 font-karla">
																	{formatDate(appointment.appointment_date)}
																</p>
															</div>
														</div>
														{getStatusBadge(appointment.status)}
													</div>

													<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
														<div className="space-y-3">
															<div className="flex items-center justify-between">
																<span className="text-sm font-medium text-gray-700 font-karla">
																	<Clock className="h-4 w-4 inline mr-1" />
																	Time:
																</span>
																<span className="text-sm text-gray-900 font-karla">
																	{formatTime(appointment.appointment_date)}
																</span>
															</div>
															<div className="flex items-center justify-between">
																<span className="text-sm font-medium text-gray-700 font-karla">
																	Duration:
																</span>
																<span className="text-sm text-gray-900 font-karla">
																	{appointment.duration_minutes || 60} minutes
																</span>
															</div>
														</div>

														<div className="space-y-3">
															{appointment.google_meet_link && (
																<div>
																	<span className="text-sm font-medium text-gray-700 font-karla block mb-1">
																		Meeting Link:
																	</span>
																	<a
																		href={appointment.google_meet_link}
																		target="_blank"
																		rel="noopener noreferrer"
																		className="text-sm text-blue-600 hover:text-blue-800 font-karla underline"
																	>
																		Join Google Meet
																	</a>
																</div>
															)}
															{appointment.notes && (
																<div>
																	<span className="text-sm font-medium text-gray-700 font-karla block mb-1">
																		Notes:
																	</span>
																	<p className="text-sm text-gray-600 font-karla">
																		{appointment.notes}
																	</p>
																</div>
															)}
														</div>
													</div>
												</div>
											</Card>
										</motion.div>
									))}
								</div>
							)
						) : conversationData.appointment ? (
							// Single appointment view (fallback to original structure)
							<div className="p-8 text-center h-full flex items-center justify-center">
								<div>
									<Video className="h-12 w-12 text-gray-400 mx-auto mb-4" />
									<h3 className="text-lg font-medium text-gray-900 font-karla mb-2">
										Meeting Information
									</h3>
									<p className="text-gray-600 font-karla mb-6">
										Virtual meeting details for this appointment
									</p>
									<div className="bg-gray-50 p-4 rounded-lg text-left max-w-md mx-auto">
										<div className="space-y-3">
											<div className="flex justify-between">
												<span className="text-sm font-medium text-gray-700 font-karla">
													Date:
												</span>
												<span className="text-sm text-gray-900 font-karla">
													{formatDate(
														conversationData.appointment.appointment_date,
													)}
												</span>
											</div>
											<div className="flex justify-between">
												<span className="text-sm font-medium text-gray-700 font-karla">
													Time:
												</span>
												<span className="text-sm text-gray-900 font-karla">
													{formatTime(
														conversationData.appointment.appointment_date,
													)}
												</span>
											</div>
											<div className="flex justify-between">
												<span className="text-sm font-medium text-gray-700 font-karla">
													Duration:
												</span>
												<span className="text-sm text-gray-900 font-karla">
													{conversationData.appointment.duration_minutes || 60}{" "}
													minutes
												</span>
											</div>
											<div className="flex justify-between items-center">
												<span className="text-sm font-medium text-gray-700 font-karla">
													Status:
												</span>
												{getStatusBadge(conversationData.appointment.status)}
											</div>
										</div>
									</div>
								</div>
							</div>
						) : (
							// No appointment at all
							<div className="flex items-center justify-center h-full">
								<div className="text-center">
									<Video className="h-12 w-12 text-gray-400 mx-auto mb-4" />
									<h3 className="text-lg font-medium text-gray-900 font-karla mb-2">
										No Meeting Scheduled
									</h3>
									<p className="text-gray-600 font-karla">
										No appointment has been scheduled for this conversation.
									</p>
								</div>
							</div>
						)}
					</div>
				)}
			</div>
		</Card>
	);
};

export default AdminMessageThread;
